<template>
  <component
    :is="args.is"
    v-bind="bindings"
    :class="classes"
    :disabled="disabled || loading"
  >
    <span :class="$style.content">
      <ui-spinner v-if="loading" :class="$style.spinner" theme="current" />
      <ui-icon v-else-if="iconLeft" :class="$style.icon" :name="iconLeft" />

      <span v-if="$slots.default" :class="$style.text"><slot /></span>

      <ui-icon v-if="iconRight" :class="$style.icon" :name="iconRight" />
    </span>
  </component>
</template>

<script lang="ts" setup>
import { useCssModule, useSlots, computed } from 'vue'
import { useButton } from '@/ui-core/composables/use-button'
import UiSpinner from '../spinner/index.vue'
import UiIcon from '../icon/index.vue'
import type { Theme, Size, Justification } from './consts'

defineOptions({
  name: 'UiButton'
})

const props = withDefaults(
  defineProps<{
    href?: string
    to?: string
    tag?: string
    block?: boolean
    disabled?: boolean
    loading?: boolean
    uppercase?: boolean
    active?: boolean
    rounded?: boolean
    fill?: boolean
    plain?: boolean
    gradientBorder?: boolean
    justification?: Justification
    theme?: Theme
    size?: Size
    iconLeft?: string
    iconRight?: string
    autoHeight?: boolean
    padless?: boolean
  }>(),
  {
    href: '',
    to: '',
    tag: 'button',
    block: false,
    disabled: false,
    loading: false,
    uppercase: false,
    active: false,
    rounded: false,
    fill: false,
    plain: false,
    gradientBorder: false,
    justification: 'center',
    theme: 'default',
    size: 'default',
    iconLeft: '',
    iconRight: '',
    autoHeight: false,
    padless: false
  }
)

defineSlots<{
  default(slotProps: Record<string, never>): any
}>()

const slots = useSlots()
const cssModule = useCssModule()

const { args, bindings } = useButton(props)

const classes = computed(() => [
  cssModule.button,
  cssModule['theme-' + props.theme],
  cssModule['size-' + props.size],
  cssModule['justification-' + props.justification],
  {
    [cssModule.block]: props.block,
    [cssModule.disabled]: props.disabled,
    [cssModule.loading]: props.loading,
    [cssModule.uppercase]: props.uppercase,
    [cssModule.active]: props.active,
    [cssModule.rounded]: props.rounded,
    [cssModule.fill]: props.fill,
    [cssModule.plain]: props.plain,
    [cssModule.autoHeight]: props.autoHeight,
    [cssModule.gradientBorder]: props.gradientBorder,
    [cssModule.padless]:
      props.plain ||
      props.padless ||
      !(slots.default || (props.iconLeft && props.iconRight))
  }
])
</script>

<style lang="scss" module>
@use 'theme';
@use 'size';

.button {
  /* Theme */
  @include theme.add(accent);
  @include theme.add(error);
  @include theme.add(filter);

  /* Size */
  @include size.add(s);
  @include size.add(l);

  border-radius: var-default(--button-border-radius);
  padding: 0 var-default(--button-padding-x);
  min-width: var-default(--button-height);
  height: var-default(--button-height);
  color: var-default(--button-color);
  user-select: none;
  display: inline-flex;
  align-items: center;
  transition: all var(--root-button-transition-duration);
  position: relative;
  border: 1px solid var-default(--button-border-color);

  &.gradientBorder {
    border: 1px solid transparent;
    background: var(--root-gradient-bg-4);
    box-shadow: inset 0px 0px 0px 100vw rgb(36, 40, 47);
  }

  @include hover-focus {
    color: var-default(--button-hover-color);
    border-color: var-default(--button-hover-color);
  }

  &.active {
    color: var-default(--button-active-color);
    border-color: var-default(--button-active-border-color);
  }

  &.disabled {
    color: var-default(--button-disabled-color);
  }

  &.rounded {
    border-radius: var(--root-button-rounded-border-radius);
  }

  &:disabled {
    pointer-events: none;
  }

  &.block {
    display: flex;
    width: 100%;
  }

  &.uppercase {
    .text {
      text-transform: uppercase;
    }
  }

  &.justification {
    &-start {
      justify-content: flex-start;
    }

    &-center {
      justify-content: center;
    }

    &-end {
      justify-content: flex-end;
    }
  }

  &.padless {
    padding: 0;
  }

  &.autoHeight {
    height: auto;
  }

  &.fill {
    color: var-default(--button-fill-color);
    background-color: var-default(--button-fill-bg);

    @include hover-focus {
      color: var-default(--button-fill-hover-color);
      background-color: var-default(--button-fill-hover-bg);
      border-color: transparent;
    }

    &.active {
      color: var-default(--button-fill-active-color);
      background-color: var-default(--button-fill-hover-bg);
      border-color: transparent;
    }

    &.disabled {
      color: var-default(--button-fill-disabled-color);
      background: var-default(--button-fill-disabled-bg);
      border-color: var-default(--button-fill-disabled-border-color);
    }
  }

  &.plain {
    border-radius: 0;
    height: auto;
    color: var-default(--button-plain-color);
    border-color: transparent;

    @include hover-focus {
      color: var-default(--button-plain-hover-color);
    }

    &.active {
      color: var-default(--button-plain-active-color);
    }

    &.disabled {
      color: var-default(--button-plain-disabled-color);
    }
  }
}

.content {
  position: relative;
  z-index: 1;
  display: flex;
  gap: var-default(--button-gap);
  align-items: center;
}

.spinner {
  --spinner-height: #{var-default(--button-icon-height)};
}

.icon {
  width: var-default(--button-icon-height);
  flex-shrink: 0;
  height: var-default(--button-icon-height);
}

.text {
  @include text-truncate;

  font-family: var-default(--button-font-family);
  font-size: var-default(--button-font-size);
  font-weight: var-default(--button-font-weight);
  line-height: var-default(--button-line-height);
  letter-spacing: var-default(--button-letter-spacing);
}
</style>
